import type { LienFile } from "pages/liens/LienTypes";
import { getVerboseServiceStatus } from "utils/format-utils";

import type { ConstantTypes, FileType } from "../../../../types/DataTypes";
import { formatCurrency } from "../../../../utils/format-utils";
import FileNumberPill from "../components/FileNumberPill";
import ServicesPills from "../components/ServicesPills";
import type { InfoPillFields } from "../InfoPillTypes";
import {
  formatClaimantName,
  formatMedicareEligibilityStatus,
  getDatesOfInjuryInfo,
  getEmployerName,
  NOT_AVAILABLE,
} from "../InfoPillTypes";

// pretty print the appeal level
const getAppealLevelLabel = (
  appealLevel: string | null | undefined,
  constants: ConstantTypes | undefined,
): string => {
  if (!appealLevel || !constants?.LIEN_APPEAL_LEVEL_ACTIONS)
    return NOT_AVAILABLE;
  return (
    constants.LIEN_APPEAL_LEVEL_ACTIONS.find(
      (level) => level.value === appealLevel,
    )?.label || appealLevel
  );
};

// format the last requested submitted value
const formatLastRequestedSubmitted = (
  value: string | null | undefined,
): string => {
  if (!value) return NOT_AVAILABLE;
  return value
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/*
 * Lien Overview page
 */
export const getLienFileInfoPillData = (
  lienFileData?: LienFile,
  fileData?: FileType,
  constants?: ConstantTypes,
): InfoPillFields => {
  if (!lienFileData) return {};

  // check if next_level_of_appeal is available
  const hasAppealInfo = Boolean(lienFileData?.next_level_of_appeal);

  const baseFields = {
    "File #": {
      value: fileData?.id ? (
        <FileNumberPill fileId={fileData.id} />
      ) : (
        NOT_AVAILABLE
      ),
      disableTooltip: true,
    },
    Claimant: {
      value: formatClaimantName(fileData?.claimants?.[0]),
      action: fileData?.section_111_claimant_url
        ? {
            type: "custom" as const,
            handler: () => {
              window.open(fileData.section_111_claimant_url || undefined);
            },
          }
        : undefined,
    },
    Client: {
      value: fileData?.client?.name || NOT_AVAILABLE,
      action: {
        type: "drawer" as const,
        drawerType: "clientInfo" as const,
      },
    },
    [getDatesOfInjuryInfo(fileData?.dates_of_injury).label]: {
      value: getDatesOfInjuryInfo(fileData?.dates_of_injury).value,
    },
    Services: {
      value: fileData?.services?.length ? (
        <ServicesPills services={fileData.services} fileId={fileData.id} />
      ) : (
        NOT_AVAILABLE
      ),
      disableTooltip: true,
    },
    Employer: {
      value: getEmployerName(fileData || {}),
    },
    "Insurance Type": {
      value:
        fileData?.insurance_types?.map((i) => i.label).join(", ") ||
        NOT_AVAILABLE,
    },
    "Medicare Eligibility Status": {
      value: formatMedicareEligibilityStatus(
        fileData?.claimants?.[0]?.is_medicare_eligible,
      ),
    },
    "Lien Status": {
      value:
        (lienFileData?.service as { status_verbose?: string })
          ?.status_verbose ||
        (lienFileData?.service?.status
          ? getVerboseServiceStatus(lienFileData.service.status)
          : NOT_AVAILABLE),
      classNames: "status unprocessed",
    },
    "File Adjuster": {
      value: fileData?.adjuster?.name || NOT_AVAILABLE,
    },
    "Total Savings": {
      value: formatCurrency(lienFileData?.total_savings || 0),
    },
  };

  // if there's no appeal info, just return base fields without lien info
  if (!hasAppealInfo) return baseFields;

  // add lien-specific fields from next_level_of_appeal
  const appealInfo = lienFileData.next_level_of_appeal;
  return {
    ...baseFields,
    "Appeal Level": {
      value: getAppealLevelLabel(appealInfo?.appeal_level, constants),
    },
    "Case #": {
      value: appealInfo?.case_number || NOT_AVAILABLE,
    },
    "Total Amount": {
      value: formatCurrency(appealInfo?.amount || 0),
    },
    "Amount Disputed": {
      value: formatCurrency(appealInfo?.amount_disputed || 0),
    },
    "Amount Due": {
      value: formatCurrency(fileData?.next_total_amount_to_pay || 0),
    },
    "Payment Due Date": {
      value: appealInfo?.payment_due_date || NOT_AVAILABLE,
    },
    "Appeal Due Date": {
      value: appealInfo?.appeal_due_date || NOT_AVAILABLE,
    },
  };
};

/*
 * Lien Detail page
 */
export const getLienDetailInfoPillData = (
  lienFileData?: LienFile,
  currentLienId?: string,
  constants?: ConstantTypes,
): InfoPillFields => {
  if (!lienFileData) return {};

  const currentLien = currentLienId
    ? lienFileData.liens.find((lien) => lien.id.toString() === currentLienId)
    : undefined;

  const baseFields = {
    "File #": {
      value: lienFileData?.file_id ? (
        <FileNumberPill fileId={Number(lienFileData.file_id)} />
      ) : (
        NOT_AVAILABLE
      ),
      disableTooltip: true,
    },
    Claimant: {
      value: formatClaimantName(lienFileData?.claimants?.[0]),
      action: lienFileData?.section_111_claimant_url
        ? {
            type: "custom" as const,
            handler: () => {
              window.open(lienFileData.section_111_claimant_url || undefined);
            },
          }
        : undefined,
    },
    "Claim Number": {
      value: currentLien?.file?.claim_numbers?.[0]?.number || NOT_AVAILABLE,
    },
    Client: {
      value: lienFileData?.client?.name || NOT_AVAILABLE,
      action: {
        type: "drawer" as const,
        drawerType: "clientInfo" as const,
      },
    },
    [getDatesOfInjuryInfo(currentLien?.date_of_injury || undefined).label]: {
      value: getDatesOfInjuryInfo(currentLien?.date_of_injury || undefined)
        .value,
    },
    Services: {
      value: currentLien?.file?.services?.length ? (
        <ServicesPills
          services={currentLien.file.services}
          fileId={currentLien.file.id}
        />
      ) : (
        NOT_AVAILABLE
      ),
      disableTooltip: true,
    },
    Employer: {
      value: lienFileData?.employer?.name || NOT_AVAILABLE,
    },
    "Medicare Eligibility Status": {
      value: formatMedicareEligibilityStatus(
        lienFileData?.claimants?.[0]?.is_medicare_eligible,
      ),
    },
    "File Adjuster": {
      value: currentLien?.file?.adjuster?.name || NOT_AVAILABLE,
    },
    [currentLien?.part_type === "C" || currentLien?.part_type === "D"
      ? "Plan Name"
      : "Case ID"]: {
      value: currentLien?.case_number || NOT_AVAILABLE,
    },
    "Total Savings": {
      value: formatCurrency(currentLien?.total_savings || 0),
    },
  };

  // check if this is a part C/D lien
  const isPartCD =
    currentLien?.part_type === "C" || currentLien?.part_type === "D";
  if (isPartCD && currentLien?.part_c_d_status) {
    return {
      ...baseFields,
      "Last Request Type": {
        value: formatLastRequestedSubmitted(
          currentLien.part_c_d_status.last_requested_submitted,
        ),
      },
      "Request Submitted On": {
        value:
          currentLien.part_c_d_status.request_submitted_on || NOT_AVAILABLE,
      },
      Amount: {
        value: formatCurrency(currentLien.part_c_d_status.amount || 0),
      },
    };
  }

  // if we're in a detailed lien view (currentLienId exists), only use currentLien's appeal info
  // otherwise, use either currentLien or lienFileData's appeal info
  const hasAppealInfo = currentLienId
    ? Boolean(currentLien?.next_level_of_appeal)
    : Boolean(
        currentLien?.next_level_of_appeal || lienFileData?.next_level_of_appeal,
      );

  // if there's no appeal info, just return base fields
  if (!hasAppealInfo) return baseFields;

  // in detailed lien view, only use currentLien's appeal info
  // otherwise fall back to lienFileData's appeal info
  const appealInfo = currentLienId
    ? currentLien?.next_level_of_appeal
    : currentLien?.next_level_of_appeal || lienFileData.next_level_of_appeal;

  // if we're in detailed view and appealInfo is null, return base fields without appeal info
  if (currentLienId && !appealInfo) return baseFields;

  return {
    ...baseFields,
    "Appeal Level": {
      value: getAppealLevelLabel(appealInfo?.appeal_level, constants),
    },
    "Case #": {
      value: currentLien?.case_number || NOT_AVAILABLE,
    },
    "Total Amount": {
      value: formatCurrency(appealInfo?.amount || 0),
    },
    // aka Total Disputed
    "Amount Disputed": {
      value: formatCurrency(appealInfo?.amount_disputed || 0),
    },
    "Amount Due": {
      value: formatCurrency(appealInfo?.amount_due || 0),
    },
    "Payment Due Date": {
      value: appealInfo?.payment_due_date || NOT_AVAILABLE,
    },
    "Appeal Due Date": {
      value: appealInfo?.appeal_due_date || NOT_AVAILABLE,
    },
  };
};
